import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import * as notify from "../../../shared/notify";

import { Checkbox } from "../../../components/form-inputs/inputs";
import { getClientPreferences, updateClient } from "../../../components/shipment-editor/lib/shipmentData";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { SLA } from "./sla";
import { CreateSlaModal } from "./create-sla-modal";
import { useModal } from "shared/hooks/use-modal";
import { SubmitModal } from "./submit-modal";
import { ViewClientsModal } from "./view-clients-modal";
import { useUpdateClientSla, useCustomSla, useAddClientSla, useAddCustomSla } from "shared/hooks/api";
import { useSettings } from "shared/hooks/use-settings";
import { SlaType } from "../lib/consts";
import { format } from "date-fns";
import { DATE_FNS_ISO_DATE_FORMAT } from "../../../shared/constants";
import { useAddStandardSla } from "shared/hooks/api/use-add-client-standard-sla";
import { isUndefined } from "lodash";

const ClientPreferences = ({ selectedClient, disableImageApproval, disablePremiumEditing, disableSendToGDSN }) => {
    const hasClientSla = useRef(false);
    const hasStartDateChanged = useRef(false);
    const initialFormValues = useRef({});
    const { settings: appSettings } = useSettings();
    const { mutateAsync: addStandardClientSla } = useAddStandardSla();
    const { mutateAsync: updateClientSla } = useUpdateClientSla();
    const { mutateAsync: addClientSla } = useAddClientSla();
    const { mutate: createCustomSla } = useAddCustomSla();
    const { data: customSlas, refetch: refreshCustomSlas } = useCustomSla();
    const { open: isCreateSlaModalOpen, onOpen: openCreateSlaModal, onClose: closeCreateSlaModal } = useModal();
    const { open: isSubmitModalOpen, onOpen: openSubmitModal, onClose: closeSubmitModal } = useModal();
    const { open: isViewClientModalOpen, onOpen: openViewClientsModal, onClose: closeViewClientsModal } = useModal();
    const [selectedClientId, setSelectedClientId] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const methods = useForm({
        mode: "onBlur",
        defaultValues: {
            hasPremiumEditing: false,
            imagesNeedClientApprovalDefault: false,
            sendToGdsn: false,
            slaType: SlaType.STANDARD,
            weeklyLimit: appSettings.slaV2Options.defaultWeeklyProductLimits,
            turnaroundDays: appSettings.slaV2Options.defaultTurnAroundTime,
            startDate: undefined,
            monthlyLimit: undefined,
            annualLimit: undefined,
            physicalWeeklyLimit: undefined,
            digitalWeeklyLimit: undefined,
            physicalMonthlyLimit: undefined,
            digitalMonthlyLimit: undefined,
            physicalAnnualLimit: undefined,
            digitalAnnualLimit: undefined,
        },
    });
    const [linkedClients, setLinkedClients] = useState([]);
    const [hasSlaValuesChanged, setHasSlaValuesChanged] = useState(false);

    const watchedValues = useWatch({ control: methods.control });
    const selectedSlaType = useWatch({
        control: methods.control,
        name: "slaType",
    });
    const selectedSlaGroupName = useWatch({
        control: methods.control,
        name: "slaGroupName",
    });
    const selectedStartDate = useWatch({
        control: methods.control,
        name: "startDate",
    });
    const hasHardStopErrors = Object.values(methods.formState.errors).some((error) => {
        return error.type !== "warning";
    });
    const basicFieldsNotFilled =
        selectedSlaType === "" || selectedSlaType === undefined || selectedStartDate === "" || selectedStartDate === undefined;

    // check if any SLA values have changed compared to initial values for custom SLA only
    useEffect(() => {
        const { hasPremiumEditing, imagesNeedClientApprovalDefault, sendToGdsn, ...slaSettings } = watchedValues;
        const {
            hasPremiumEditing: initHasPremiumEditing,
            imagesNeedClientApprovalDefault: initApproval,
            sendToGdsn: initGdsn,
            ...initialSlaSettings
        } = initialFormValues.current;

        if (!isUndefined(initialFormValues.current.slaType) && initialFormValues.current.slaType !== watchedValues.slaType) {
            setHasSlaValuesChanged(true);

            return;
        }

        if (watchedValues.slaType === SlaType.CUSTOM) {
            const changed = Object.keys(initialSlaSettings).some((key) => {
                if (key === "slaGroupName") {
                    const initialValue = initialSlaSettings[key] ? initialSlaSettings[key].value : undefined;
                    const currentValue = slaSettings[key] ? slaSettings[key].value : undefined;

                    return initialValue !== currentValue;
                }

                if (key === "startDate" && !isUndefined(initialSlaSettings[key])) {
                    const initialDate = initialSlaSettings[key] ? format(new Date(initialSlaSettings[key]), DATE_FNS_ISO_DATE_FORMAT) : undefined;
                    const currentDate = slaSettings[key] ? format(new Date(slaSettings[key]), DATE_FNS_ISO_DATE_FORMAT) : undefined;

                    hasStartDateChanged.current = initialDate !== currentDate;

                    return false;
                }

                return slaSettings[key] !== initialSlaSettings[key];
            });

            if (changed) {
                setHasSlaValuesChanged(changed);
            }
        }
    }, [watchedValues, methods]);

    const handleDelayedFormReValidate = useCallback(() => {
        setTimeout(() => {
            methods.trigger();
        }, 1);
    }, [methods]);

    const isSaveButtonDisabled = () => {
        if (hasHardStopErrors) {
            return true;
        }

        if (methods.getValues().slaType === SlaType.STANDARD) {
            return false;
        }

        if (selectedSlaType === SlaType.CUSTOM) {
            return basicFieldsNotFilled || selectedSlaGroupName === undefined || selectedSlaGroupName?.value === undefined;
        }

        return basicFieldsNotFilled;
    };

    const getLinkedSlaGroup = useCallback(
        (groupName) => {
            return customSlas?.find(({ id }) => id === groupName);
        },
        [customSlas]
    );

    const isClientLinkedToSlaGroup = useMemo(() => {
        const sla = customSlas?.find(({ id }) => id === selectedSlaGroupName?.value);

        return sla && sla.clientSlas.some(({ clientId }) => clientId === selectedClientId);
    }, [customSlas, selectedClientId, selectedSlaGroupName?.value]);
    const linkedSla = useMemo(() => {
        return getLinkedSlaGroup(selectedSlaGroupName?.value);
    }, [getLinkedSlaGroup, selectedSlaGroupName?.value]);
    const linkedClientsToGroup = linkedSla ? linkedSla?.clientSlas : [];

    const handleResetSlaFields = useCallback(() => {
        methods.reset({
            ...methods.getValues(),
            slaType: SlaType.STANDARD,
            turnaroundDays: appSettings.slaV2Options.defaultTurnAroundTime,
            weeklyLimit: appSettings.slaV2Options.defaultWeeklyProductLimits,
            monthlyLimit: undefined,
            annualLimit: undefined,
            physicalWeeklyLimit: undefined,
            digitalWeeklyLimit: undefined,
            physicalMonthlyLimit: undefined,
            digitalMonthlyLimit: undefined,
            physicalAnnualLimit: undefined,
            digitalAnnualLimit: undefined,
        });
    }, [appSettings.slaV2Options.defaultTurnAroundTime, appSettings.slaV2Options.defaultWeeklyProductLimits, methods]);

    const handleUpdateCustomSla = useCallback(() => {
        if (linkedSla) {
            methods.reset({
                ...methods.getValues(),
                slaType: SlaType.CUSTOM,
                turnaroundDays: linkedSla.turnaroundDays,
                weeklyLimit: linkedSla.weeklyLimit,
                monthlyLimit: linkedSla.monthlyLimit,
                annualLimit: linkedSla.annualLimit,
                physicalWeeklyLimit: linkedSla.physicalWeeklyLimit,
                digitalWeeklyLimit: linkedSla.digitalWeeklyLimit,
                physicalMonthlyLimit: linkedSla.physicalMonthlyLimit,
                digitalMonthlyLimit: linkedSla.digitalMonthlyLimit,
                physicalAnnualLimit: linkedSla.physicalAnnualLimit,
                digitalAnnualLimit: linkedSla.digitalAnnualLimit,
                anyLimitProvided: linkedSla.anyLimitProvided,
            });
            handleDelayedFormReValidate();
        }
    }, [linkedSla, methods, handleDelayedFormReValidate]);

    const handleChangeSlaGroup = useCallback(
        (selectedGroup) => {
            const group = getLinkedSlaGroup(selectedGroup.value);

            if (group) {
                methods.reset({
                    ...methods.getValues(),
                    slaType: SlaType.CUSTOM,
                    turnaroundDays: group.turnaroundDays,
                    weeklyLimit: group.weeklyLimit,
                    monthlyLimit: group.monthlyLimit,
                    annualLimit: group.annualLimit,
                    physicalWeeklyLimit: group.physicalWeeklyLimit,
                    digitalWeeklyLimit: group.digitalWeeklyLimit,
                    physicalMonthlyLimit: group.physicalMonthlyLimit,
                    digitalMonthlyLimit: group.digitalMonthlyLimit,
                    physicalAnnualLimit: group.physicalAnnualLimit,
                    digitalAnnualLimit: group.digitalAnnualLimit,
                    anyLimitProvided: group.anyLimitProvided,
                });
                handleDelayedFormReValidate();
            }
        },
        [getLinkedSlaGroup, methods, handleDelayedFormReValidate]
    );

    useEffect(() => {
        const fetchClientPreferences = async () => {
            if (selectedClient) {
                const clientPreferences = await getClientPreferences({
                    clientCode: selectedClient,
                });

                if (!clientPreferences) {
                    return;
                }

                hasClientSla.current = clientPreferences.sla !== null;
                setSelectedClientId(clientPreferences.id);

                if (!hasClientSla.current) {
                    const values = {
                        hasPremiumEditing: clientPreferences.hasPremiumEditing,
                        imagesNeedClientApprovalDefault: clientPreferences.imagesNeedClientApprovalDefault,
                        sendToGdsn: clientPreferences.sendToGdsn,
                        slaType: SlaType.STANDARD,
                        weeklyLimit: appSettings.slaV2Options.defaultWeeklyProductLimits,
                        turnaroundDays: appSettings.slaV2Options.defaultTurnAroundTime,
                    };
                    methods.reset(values);
                    initialFormValues.current = values;
                } else {
                    const { clientId, slaId, clientName, shortName, weeklyLimit, turnaroundDays, description, ...sla } = clientPreferences.sla;
                    const values = {
                        hasPremiumEditing: clientPreferences.hasPremiumEditing,
                        imagesNeedClientApprovalDefault: clientPreferences.imagesNeedClientApprovalDefault,
                        sendToGdsn: clientPreferences.sendToGdsn,
                        slaGroupName:
                            sla.slaType === SlaType.CUSTOM
                                ? {
                                      label: shortName,
                                      value: slaId,
                                  }
                                : undefined,
                        weeklyLimit: sla.slaType === SlaType.STANDARD ? appSettings.slaV2Options.defaultWeeklyProductLimits : weeklyLimit,
                        turnaroundDays: sla.slaType === SlaType.STANDARD ? appSettings.slaV2Options.defaultTurnAroundTime : turnaroundDays,
                        ...sla,
                    };
                    methods.reset(values);
                    initialFormValues.current = values;

                    handleDelayedFormReValidate();
                }
            }
        };

        fetchClientPreferences();

        return () => {
            setHasSlaValuesChanged(false);
            hasStartDateChanged.current = false;
        };
    }, [
        appSettings.slaV2Options.defaultTurnAroundTime,
        appSettings.slaV2Options.defaultWeeklyProductLimits,
        handleDelayedFormReValidate,
        methods,
        selectedClient,
    ]);

    const updateField = async (name, value) => {
        if (isSubmitting) {
            return;
        }

        if (selectedClient === null) {
            notify.error("Please select the client");

            return;
        }

        methods.setValue(name, value);
    };

    const onSaveWithErrors = (errors) => {
        const isEveryFieldWarning = Object.keys(errors).every((key) => errors[key].type === "warning");

        if (isEveryFieldWarning) {
            onSavePreferences(methods.getValues());
        } else {
            methods.handleSubmit(onSavePreferences)();
        }

        if (isSubmitModalOpen) {
            closeSubmitModal();
        }
    };

    const onSavePreferences = async (formValues) => {
        const { hasPremiumEditing, imagesNeedClientApprovalDefault, sendToGdsn, slaType, slaGroupName, startDate, ...values } = formValues;
        setIsSubmitting(true);

        const payload = {
            code: selectedClient,
            hasPremiumEditing,
            imagesNeedClientApprovalDefault,
            sendToGdsn,
        };

        const settings = {
            noRedirect: true,
        };

        const response = await updateClient(payload, settings);

        if (isSubmitModalOpen) {
            closeSubmitModal();
        }

        let isSlaUpdateSuccessful;

        if (hasSlaValuesChanged || hasStartDateChanged.current) {
            if (slaType === SlaType.STANDARD) {
                const res = await addStandardClientSla({
                    clientId: selectedClientId,
                });
                isSlaUpdateSuccessful = res !== null;
                hasClientSla.current = false;
            } else {
                if (hasClientSla.current) {
                    const res = await updateClientSla({
                        clientId: selectedClientId,
                        slaId: slaGroupName.value,
                        startDate: format(new Date(startDate), DATE_FNS_ISO_DATE_FORMAT),
                        ...values,
                    });
                    isSlaUpdateSuccessful = res !== null;
                } else {
                    const res = await addClientSla({
                        clientId: selectedClientId,
                        slaId: slaGroupName.value,
                        startDate: format(new Date(startDate), DATE_FNS_ISO_DATE_FORMAT),
                        ...values,
                    });
                    hasClientSla.current = true;
                    isSlaUpdateSuccessful = res !== null;
                }
            }
        }

        if (response && response.success === false) {
            setIsSubmitting(false);

            return notify.error("You do not have the permissions to perform this action.");
        }

        if (!response) {
            setIsSubmitting(false);

            return;
        }

        if (response) {
            if (isSlaUpdateSuccessful || isUndefined(isSlaUpdateSuccessful)) {
                setHasSlaValuesChanged(false);
                hasStartDateChanged.current = false;
                initialFormValues.current = formValues;
                notify.success("Updated successfully");
            }
        }

        setIsSubmitting(false);
        await refreshCustomSlas();
    };

    const handleCreateNewCustomSla = (payload) => {
        createCustomSla(
            { id: selectedClient, ...payload },
            {
                onSuccess: refreshCustomSlas,
            }
        );
    };

    const handleSave = () => {
        if (linkedClientsToGroup.length > 1 && selectedSlaType === SlaType.CUSTOM && hasSlaValuesChanged) {
            openSubmitModal();
        } else {
            methods.handleSubmit(onSavePreferences, onSaveWithErrors)();
        }
    };

    const handleViewClients = (relatedClients) => {
        setLinkedClients(relatedClients);
        openViewClientsModal();
    };

    return (
        <>
            <FormProvider {...methods}>
                <form>
                    <div className="m-t-20 m-b-20">
                        <div className="separator -no-margin" />
                    </div>
                    <h2>Settings</h2>
                    <div>
                        <h3>
                            <Controller
                                name="hasPremiumEditing"
                                control={methods.control}
                                render={({ field }) => (
                                    <Checkbox
                                        {...field}
                                        label="Premium Editing"
                                        updateOnChange={updateField}
                                        disabled={disablePremiumEditing}
                                    />
                                )}
                            />
                        </h3>
                        <h3>
                            <Controller
                                name="imagesNeedClientApprovalDefault"
                                control={methods.control}
                                render={({ field }) => (
                                    <Checkbox
                                        {...field}
                                        label="Client Image Approval"
                                        updateOnChange={updateField}
                                        disabled={disableImageApproval}
                                    />
                                )}
                            />
                        </h3>
                        <h3>
                            <Controller
                                name="sendToGdsn"
                                control={methods.control}
                                render={({ field }) => (
                                    <Checkbox
                                        {...field}
                                        label="Send to GDSN"
                                        updateOnChange={updateField}
                                        disabled={disableSendToGDSN}
                                    />
                                )}
                            />
                        </h3>
                    </div>
                    <div>
                        <div className="m-t-20 m-b-20">
                            <div className="separator -no-margin" />
                        </div>
                        <SLA
                            selectedClientId={selectedClientId}
                            isClientLinkedToSlaGroup={isClientLinkedToSlaGroup}
                            linkedClients={linkedClientsToGroup}
                            customSlaOptions={customSlas}
                            onOpenCreateNewSlaModal={openCreateSlaModal}
                            onOpenViewClients={handleViewClients}
                            onResetSlaFields={handleResetSlaFields}
                            onUpdateCustomSla={handleUpdateCustomSla}
                            onChangeSlaGroup={handleChangeSlaGroup}
                        />
                    </div>
                    <div className="m-t-20 m-b-20">
                        <div className="separator -no-margin" />
                    </div>
                    <button
                        type="button"
                        disabled={isSaveButtonDisabled() || isSubmitting}
                        className="button -primary"
                        onClick={handleSave}
                    >
                        Save
                    </button>
                </form>
            </FormProvider>
            <CreateSlaModal
                onClose={closeCreateSlaModal}
                open={isCreateSlaModalOpen}
                onCreateNewCustomSla={handleCreateNewCustomSla}
            />
            <SubmitModal
                open={isSubmitModalOpen}
                onClose={closeSubmitModal}
                onConfirm={methods.handleSubmit(onSavePreferences, onSaveWithErrors)}
            />
            <ViewClientsModal
                selectedGroup={selectedSlaGroupName?.label}
                open={isViewClientModalOpen}
                onClose={closeViewClientsModal}
                linkedClients={linkedClients}
            />
        </>
    );
};

export default ClientPreferences;
